import React, { useEffect, useState, useRef } from "react";
import API from "../services/api";
import { useParams, Link } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import html2canvas from "html2canvas";
import Header from "../components/Header";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;


// const API_BASE = import.meta.env.REACT_APP_API_URL;
const API_BASE = process.env.REACT_APP_API_URL;


function EpaperViewer() {
  const { id } = useParams();

  const [paper, setPaper] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);

  const [cropMode, setCropMode] = useState(false);
  const [selection, setSelection] = useState(null);
  const [dragging, setDragging] = useState(false);

  const viewerRef = useRef();
  const containerRef = useRef();

  // FETCH PAPER
  const fetchPaper = async () => {
    try {
      const res = await API.get(`/epapers/${id}`);
      setPaper(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchPaper();
    // eslint-disable-next-line
  }, [id]);

  const onLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  // Helper to generate branded canvas and download
  const drawBrandedAndDownload = async (sourceCanvas, rect, isCrop) => {
    const scale = 2;
    const { x: cropX, y: cropY, width: cropW, height: cropH } = rect;

    // Heights for our custom branding
    const headerH = 80 * scale;
    const footerH = 60 * scale;

    // Create actual output canvas
    const outCanvas = document.createElement("canvas");
    outCanvas.width = Math.max(cropW, 500 * scale); // Ensure minimum width for header readability
    outCanvas.height = cropH + headerH + footerH;

    const ctx = outCanvas.getContext("2d");

    // 1. Draw Background (white for the whole canvas)
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, outCanvas.width, outCanvas.height);

    // 2. Load Logo
    const imgWrapper = new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = "/news.png"; // Your Pratyaksha News logo
    });

    const logoImg = await imgWrapper;

    // 3. Draw Centered Logo and Title
    if (logoImg) {
      const targetLogoH = 45 * scale;
      const aspect = logoImg.width / logoImg.height;
      const targetLogoW = targetLogoH * aspect;

      const logoY = (headerH - targetLogoH) / 2;

      ctx.font = `bold ${32 * scale}px sans-serif`;
      const titleText = "PRATYAKSHA NEWS";
      const textWidth = ctx.measureText(titleText).width;
      const spacing = 15 * scale;

      // Calculate start X to center the group (logo + spacing + text)
      const totalW = targetLogoW + spacing + textWidth;
      const startX = (outCanvas.width - totalW) / 2;

      ctx.drawImage(logoImg, startX, logoY, targetLogoW, targetLogoH);

      ctx.fillStyle = "#0b5cff"; // Blue text
      ctx.textBaseline = "middle";
      ctx.textAlign = "left";
      ctx.fillText(titleText, startX + targetLogoW + spacing, headerH / 2);
    } else {
      ctx.fillStyle = "#0b5cff";
      ctx.font = `bold ${32 * scale}px sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("PRATYAKSHA NEWS", outCanvas.width / 2, headerH / 2);
    }

    // 4. Draw the Cropped Canvas Portion
    const drawX = (outCanvas.width - cropW) / 2; // center crop if output width > crop width
    ctx.drawImage(
      sourceCanvas,
      cropX, cropY, cropW, cropH,
      drawX, headerH, cropW, cropH
    );

    // 5. Draw Footer Info (Centered, White background)
    ctx.fillStyle = "#003399"; // Dark blue text similar to Sakshi
    ctx.font = `bold ${12 * scale}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const publishDate = new Date(paper.publish_date).toLocaleDateString('en-IN');
    const line1 = `${publishDate} | ${paper.edition} Edition | Page : ${page}`;
    const line2 = `Source : https://www.pratyakshanews.com/`;

    const footerCenterY = headerH + cropH + (footerH / 2);
    ctx.fillText(line1, outCanvas.width / 2, footerCenterY - (8 * scale));
    ctx.fillText(line2, outCanvas.width / 2, footerCenterY + (10 * scale));

    // Finally download image
    const link = document.createElement("a");
    link.download = `pratyaksha_news_${isCrop ? 'crop' : `page_${page}`}_${Date.now()}.png`;
    link.href = outCanvas.toDataURL("image/png");
    link.click();
  };

  // FULL SCREENSHOT
  const captureFull = async () => {
    if (!viewerRef.current) return;
    const canvas = await html2canvas(viewerRef.current, { scale: 2, useCORS: true, allowTaint: true });

    await drawBrandedAndDownload(canvas, {
      x: 0, y: 0, width: canvas.width, height: canvas.height
    }, false);
  };

  // CROP SCREENSHOT
  const captureCrop = async () => {
    if (!selection || !viewerRef.current) return;

    // We capture the whole viewer then slice it based on coordinates
    // data-html2canvas-ignore="true" on UI elements prevents them from being captured
    const canvas = await html2canvas(viewerRef.current, { scale: 2, useCORS: true, allowTaint: true });

    const scale = 2;
    // Extract precise coordinates natively avoiding negative sizes
    const cropX = Math.max(0, selection.x * scale);
    const cropY = Math.max(0, selection.y * scale);
    const cropW = Math.min(canvas.width - cropX, selection.width * scale);
    const cropH = Math.min(canvas.height - cropY, selection.height * scale);

    if (cropW <= 0 || cropH <= 0) {
      setSelection(null);
      setCropMode(false);
      return;
    }

    await drawBrandedAndDownload(canvas, { x: cropX, y: cropY, width: cropW, height: cropH }, true);

    setSelection(null);
    setCropMode(false);
  };

  // CROP EVENTS
  const handleMouseDown = (e) => {
    if (!cropMode) return;
    e.preventDefault();

    const rect = viewerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setSelection({ startX: x, startY: y, x, y, width: 0, height: 0 });
    setDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!dragging || !cropMode) return;

    const rect = viewerRef.current.getBoundingClientRect();
    const currentX = e.clientX - rect.left;
    const currentY = e.clientY - rect.top;

    setSelection((prev) => {
      const width = Math.abs(currentX - prev.startX);
      const height = Math.abs(currentY - prev.startY);
      const x = Math.min(currentX, prev.startX);
      const y = Math.min(currentY, prev.startY);
      return { ...prev, x, y, width, height };
    });
  };

  // When dragging outside document
  useEffect(() => {
    const handleGlobalMouseUp = () => setDragging(false);
    document.addEventListener("mouseup", handleGlobalMouseUp);
    return () => document.removeEventListener("mouseup", handleGlobalMouseUp);
  }, []);

  if (!paper) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-950 text-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100 font-sans text-gray-800 overflow-hidden">
      <div className="shrink-0 z-20 shadow-sm">
        <Header />
      </div>

      {/* Sakshi-Style Secondary Toolbar */}
      <div className="bg-white h-14 flex items-center justify-between px-4 md:px-6 border-b border-gray-300 shadow-sm shrink-0 z-10 relative">

        {/* Left: Info */}
        <div className="flex items-center space-x-4">
          <Link to="/" className="text-news-blue hover:text-blue-800 transition group flex items-center font-semibold text-sm">
            <svg className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
            Home
          </Link>
          <div className="h-5 w-px bg-gray-300 hidden sm:block"></div>
          <div className="hidden sm:block">
            <h1 className="text-sm font-bold text-gray-800 leading-tight">{paper.title}</h1>
            <p className="text-[10px] text-news-red uppercase tracking-wider font-bold">{paper.edition} | {new Date(paper.publish_date).toLocaleDateString()}</p>
          </div>
        </div>

        {/* Right: Toolbar Tools */}
        <div className="flex items-center space-x-3">

          {/* Page Selector Dropdown */}
          <div className="flex items-center bg-gray-50 border border-gray-300 rounded px-2 py-1">
            <span className="text-xs font-bold text-gray-600 mr-2 hidden md:inline">Page:</span>
            <select
              value={page}
              onChange={(e) => setPage(Number(e.target.value))}
              className="bg-transparent text-sm font-semibold text-gray-800 outline-none cursor-pointer"
            >
              {Array.from(new Array(numPages), (el, index) => (
                <option key={index} value={index + 1}>{index + 1} of {numPages}</option>
              ))}
            </select>
          </div>

          <div className="h-5 w-px bg-gray-300 hidden sm:block"></div>

          {/* Zoom Controls */}
          <div className="flex items-center bg-gray-100 border border-gray-300 rounded">
            <button onClick={() => setZoom((z) => Math.max(0.4, z - 0.2))} className="p-1.5 hover:bg-gray-200 transition text-gray-700 rounded-l" title="Zoom Out">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7"></path></svg>
            </button>
            <span className="text-xs font-bold px-2 border-x border-gray-300 text-gray-700 flex items-center justify-center min-w-[3rem]">
              {Math.round(zoom * 100)}%
            </span>
            <button onClick={() => setZoom((z) => Math.min(3, z + 0.2))} className="p-1.5 hover:bg-gray-200 transition text-gray-700 rounded-r" title="Zoom In">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"></path></svg>
            </button>
          </div>

          {/* Crop Button */}
          <button
            onClick={() => { setCropMode(!cropMode); setSelection(null); }}
            className={`flex items-center px-3 py-1.5 rounded text-xs font-bold transition shadow-sm border ${cropMode ? "bg-news-red text-white border-news-red" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
            title="Crop & Share"
          >
            <svg className="w-3.5 h-3.5 sm:mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5"></path></svg>
            <span className="hidden sm:inline">{cropMode ? "Cancel" : "Crop"}</span>
          </button>

          {/* Save Button */}
          <button
            onClick={captureFull}
            className="flex items-center px-3 py-1.5 bg-news-blue hover:bg-[#0b5cff] border border-transparent rounded text-white text-xs font-bold transition shadow-sm"
            title="Download Page"
          >
            <svg className="w-3.5 h-3.5 sm:mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
            <span className="hidden sm:inline">Save</span>
          </button>

        </div>
      </div>

      <div className="flex flex-1 overflow-hidden relative bg-[#e5e7eb]">

        {/* Main Viewer Area */}
        <div ref={containerRef} className="flex-1 overflow-auto flex justify-center p-4 relative styled-scrollbar cursor-grab active:cursor-grabbing">

          {/* Floating Navigation Buttons */}
          {page > 1 && (
            <button
              onClick={() => setPage(p => p - 1)}
              className="sticky left-4 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-100 text-news-blue border border-gray-300 rounded-full p-2 md:p-3 shadow-lg z-30 transition opacity-80 hover:opacity-100"
              title="Previous Page"
            >
              <svg className="w-6 h-6 md:w-8 md:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M15 19l-7-7 7-7" /></svg>
            </button>
          )}

          <div className="flex-1 flex justify-center items-start min-w-max">
            <div
              ref={viewerRef}
              className={`relative inline-block bg-white shadow-2xl transition-transform mx-auto ${cropMode ? "cursor-crosshair" : "cursor-auto"}`}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
            >
              <Document
                file={`${window.location.origin}${API_BASE}/epapers/download/${paper.id}`}
                onLoadSuccess={onLoadSuccess}
              >
                <Page
                  pageNumber={page}
                  scale={zoom}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>

              {/* Dark Overlay for Crop Mode */}
              {cropMode && (
                <div
                  data-html2canvas-ignore="true"
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "rgba(0,0,0,0.3)",
                    pointerEvents: "none"
                  }}
                />
              )}

              {/* Crop Selection Box */}
              {selection && cropMode && selection.width > 0 && selection.height > 0 && (
                <div
                  data-html2canvas-ignore="true"
                  style={{
                    position: "absolute",
                    left: selection.x,
                    top: selection.y,
                    width: selection.width,
                    height: selection.height,
                    border: "2px dashed #3b82f6",
                    background: "rgba(59, 130, 246, 0.1)",
                    pointerEvents: "none"
                  }}
                >
                  {/* Save Crop Tooltip/Button */}
                  <div
                    className="absolute bottom-0 -right-12 flex flex-col gap-1 pointer-events-auto bg-gray-900 bg-opacity-80 p-1.5 rounded shadow-lg"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={(e) => { e.stopPropagation(); captureCrop(); }}
                      className="bg-green-500 text-white rounded p-1.5 hover:bg-green-600 transition"
                      title="Download Clip"
                    >
                      <svg className="w-5 h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelection(null); setCropMode(false); }}
                      className="bg-red-500 text-white rounded p-1.5 hover:bg-red-600 transition"
                      title="Cancel Crop"
                    >
                      <svg className="w-5 h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Floating Next Button */}
          {page < numPages && (
            <button
              onClick={() => setPage(p => p + 1)}
              className="sticky right-4 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-100 text-news-blue border border-gray-300 rounded-full p-2 md:p-3 shadow-lg z-30 transition opacity-80 hover:opacity-100 ml-auto"
              title="Next Page"
            >
              <svg className="w-6 h-6 md:w-8 md:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M9 5l7 7-7 7" /></svg>
            </button>
          )}

        </div>


      </div>



    </div>
  );
}

export default EpaperViewer;